// Mapper + helper functions extracted from serviceLayerGateway.ts for
// guard-file-size compliance (GUARD-S1 touch-it-own-it). Pure functions only —
// no network, no state.

import type { Mission, MissionLead } from '../../api/missions-types';
import type { RepTargets } from './types';
import { missionCreateSchema, missionLeadSchema, missionSchema } from './schemas';
import { mapDbStatusToUi, mapUiStatusToDb } from '../../status/missionStatus';
import { getAuthToken } from '../../http/serviceLayerClient';

export type RepTargetsResponse = {
  visits_per_day: number;
  demos_per_day: number;
  orders_per_day: number;
  revenue_per_day_eur: number;
  demo_rate_threshold_pct: number;
};

export const DEFAULT_REP_TARGETS: RepTargets = {
  visitsPerDay: 15,
  demosPerDay: 6,
  ordersPerDay: 1,
  revenuePerDayEur: 500,
  demoRateThresholdPct: 40,
};


export function normalizeMissionRow(row: any): any {
  // Normalize database values to match schema expectations
  const normalized: any = { ...row };

  // Normalize status: database may have "Planned", "In Progress" etc.
  if (normalized.status) {
    const statusLower = normalized.status.toLowerCase().replace(/\s+/g, '_');
    // Map common variations to schema values
    if (statusLower === 'planned' || statusLower === 'plan') {
      normalized.status = 'planned';
    } else if (statusLower === 'in_progress' || statusLower === 'inprogress' || statusLower === 'in progress') {
      normalized.status = 'in_progress';
    } else if (statusLower === 'completed' || statusLower === 'done' || statusLower === 'finished') {
      normalized.status = 'completed';
    } else if (statusLower === 'cancelled' || statusLower === 'canceled') {
      normalized.status = 'cancelled';
    } else if (statusLower === 'draft') {
      normalized.status = 'draft';
    } else {
      // Default to planned if unknown
      normalized.status = 'planned';
    }
  } else {
    normalized.status = 'planned';
  }

  // Normalize assigned_day: ensure it's one of the valid German weekday names or null
  if (normalized.assigned_day) {
    const validDays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];
    if (!validDays.includes(normalized.assigned_day)) {
      // Try to map common variations
      const dayLower = normalized.assigned_day.toLowerCase();
      if (dayLower.includes('montag') || dayLower === 'monday') normalized.assigned_day = 'Montag';
      else if (dayLower.includes('dienstag') || dayLower === 'tuesday') normalized.assigned_day = 'Dienstag';
      else if (dayLower.includes('mittwoch') || dayLower === 'wednesday') normalized.assigned_day = 'Mittwoch';
      else if (dayLower.includes('donnerstag') || dayLower === 'thursday') normalized.assigned_day = 'Donnerstag';
      else if (dayLower.includes('freitag') || dayLower === 'friday') normalized.assigned_day = 'Freitag';
      else {
        // If can't map, set to null (optional field)
        normalized.assigned_day = null;
      }
    }
  } else {
    normalized.assigned_day = null;
  }

  // Preserve null assigned_date (unassigned missions).
  // Only default to null if the field is completely absent (undefined).
  if (normalized.assigned_date === undefined) {
    normalized.assigned_date = null;
  }

  return normalized;
}

export function toUiMission(row: any): Mission {
  // Normalize before schema validation
  const normalized = normalizeMissionRow(row);
  const parsed = missionSchema.parse(normalized);
  return {
    id: parsed.id,
    title: parsed.title,
    date: parsed.assigned_date,
    region: parsed.region_id || undefined,
    ownerEmail: parsed.owner_email,
    status: mapDbStatusToUi(parsed.status || 'planned'),
    archived: false,
    // ROUTE-OPTIMIZATION-GUIDE (7c2ec578) — pass through precomputed
    // route fields. Previously these were declared on the Mission type
    // but the mapper dropped them, leaving FE consumers reading
    // undefined and drawRoute=false even when SL had the polyline.
    route_polyline: parsed.route_polyline ?? null,
    route_distance_km: parsed.route_distance_km ?? null,
    route_duration_min: parsed.route_duration_min ?? null,
    route_variants: parsed.route_variants ?? null,
  };
}

export function toDbMissionPayload(newMission: Omit<Mission, 'id'>) {
  return missionCreateSchema.parse({
    title: newMission.title,
    assigned_date: newMission.date,
    region_id: newMission.region,
    owner_email: newMission.ownerEmail,
    status: mapUiStatusToDb(newMission.status),
    slot_index: 0,
  });
}

export function deriveTier(stop: any): 'bestandskunde' | 'qualifiziert' | 'kaltbesuch' {
  if (stop.lead_type === 'bestandskunde') return 'bestandskunde';
  const briefing = stop.lead_briefing;
  if (briefing?.is_bestandskunde) return 'bestandskunde';
  if (briefing?.products?.lead?.score && briefing.products.lead.score > 0) return 'qualifiziert';
  return 'kaltbesuch';
}

/** Map SL lifecycle `state` (authoritative) to UI status, falling back to `status` field */
export function mapLeadState(state?: string | null, status?: string | null): string | undefined {
  // SL `state` is the source of truth when present and not just "pending"
  if (state && state !== 'pending') {
    switch (state) {
      case 'visited': return 'Visited';
      case 'skipped': return 'Skipped';
      case 'rejected': return 'Skipped'; // rejected maps to Skipped bucket
      case 'bounced': return 'Skipped';
      default: return status || undefined;
    }
  }
  return status || undefined;
}

export function toUiMissionLead(row: any): MissionLead {
  try {
    // Try safeParse first to get detailed error information
    const parseResult = missionLeadSchema.safeParse(row);
    if (!parseResult.success) {
      console.error('[toUiMissionLead] Validation failed:', parseResult.error.errors);
      throw parseResult.error;
    }

    const parsed = parseResult.data;
    // Backend returns unified_lead_id, but we map to leadId for UI consistency
    const leadId = parsed.unified_lead_id ?? parsed.lead_id;
    if (!leadId) {
      throw new Error('Mission lead must have either lead_id or unified_lead_id');
    }
    const result: MissionLead = {
      id: parsed.id,
      missionId: parsed.mission_id,
      leadId: leadId,
      orderIndex: parsed.order_index ?? 0,
      plannedTime: parsed.planned_time || undefined,
      status: mapLeadState(parsed.state, parsed.status),
      tier: deriveTier(row),
      is_anchor: parsed.is_anchor ?? false,
      anchor_source: parsed.anchor_source || undefined,
      preferred_time: parsed.preferred_time || undefined,
      pinned: parsed.is_anchor ?? false,
      // FR-FE-JOKER-AS-VISIT-FLOW-2026-06-10: render-only passthrough so the
      // board can tag a joker stop from the mission_lead itself (NOT the
      // legacy joker_stops table). Additive — absent on non-joker stops.
      stop_type: parsed.stop_type ?? undefined,
      joker_reason: parsed.joker_reason ?? undefined,
      joker_source: parsed.joker_source ?? undefined,
      // FR-FE-JOKER-COMPANY-NAME-2026-06-24: carry SL's top-level company_name so
      // a joker / rep-discovered-company stop (no unified_lead, no lead_briefing)
      // renders its name instead of the raw leadId / "Unbekannt".
      company_name: parsed.company_name ?? undefined,
    };
    // Pass through enriched lead data from stops endpoint (if present)
    if (parsed.unified_lead && typeof parsed.unified_lead === 'object') {
      result._rawUnifiedLead = parsed.unified_lead as Record<string, unknown>;
    }
    // Pass through SL lifecycle fields for bucket sorting (rejection_reason, state)
    if (row.rejection_reason || row.state) {
      if (!result._rawUnifiedLead) result._rawUnifiedLead = {};
      if (row.rejection_reason) result._rawUnifiedLead.rejection_reason = row.rejection_reason;
      if (row.state) result._rawUnifiedLead.state = row.state;
    }
    // Always pass through lead_briefing for downstream consumers (LeadDetailModal)
    if (parsed.lead_briefing && typeof parsed.lead_briefing === 'object') {
      const briefing = parsed.lead_briefing as Record<string, any>;
      result.lead_briefing = briefing;
      // Extract lead data into _rawUnifiedLead if not already present
      if (!result._rawUnifiedLead) {
        const company = briefing.company as Record<string, any> | undefined;
        if (company) {
          result._rawUnifiedLead = {
            id: leadId,
            firma: company.name,
            adresse: company.address,
            plz: company.plz,
            ort: company.ort,
            telefon: company.phone,
            website: company.website,
            employees: company.employees,
            trade_description: company.trade_description,
            score: briefing.score,
            coordinates: company.coordinates,
          };
        }
      }
    }
    return result;
  } catch (err) {
    console.error('[toUiMissionLead] Parse failed:', err);
    throw err;
  }
}


/**
 * Unwrap service layer envelope: { success: true, data: T } → T
 * Falls back to raw value if not wrapped.
 */
export function unwrapEnvelope<T = any>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw && 'success' in raw) {
    return (raw as any).data as T;
  }
  return raw as T;
}

/**
 * Extract the raw mission rows from a GET /v1/missions response body,
 * tolerating the Service Layer envelope ({success, data: [...]}), the legacy
 * ({items: [...]}) shape, and a bare array.
 */
export function extractMissionListItems(data: unknown): any[] {
  if (data && typeof data === 'object') {
    if ('data' in data && Array.isArray((data as any).data)) return (data as any).data;
    if ('items' in data && Array.isArray((data as any).items)) return (data as any).items;
    if (Array.isArray(data)) return data;
    console.warn('[MissionsGateway] Unexpected data format:', data);
  }
  return [];
}

export async function buildCtx() {
  const token = await getAuthToken();
  const user = (window as any).__USER__ as { email: string | null } | undefined;
  // For superuser access, we rely on cookie-based auth (no Bearer token needed)
  // The service layer will read the role from the JWT in the cookie
  return { token, actorEmail: user?.email || null };
}
